import { type SEOHandle } from '@nasa-gcn/remix-seo'
import { marked } from 'marked'
import { data } from 'react-router'
import { type Route } from './+types/chat-widget-terms-of-use.ts'

const MetaTitle = 'Chat Widget Terms of Use | Dealer Pro Edge'
const MetaDescription =
	'Terms governing use of the live chat, AI-assisted chat, messaging, and related communication features made available through the Dealer Pro Edge chat widget.'

export const handle: SEOHandle = {
	getSitemapEntries: () => null,
}

export const meta: Route.MetaFunction = ({ matches }) => {
	const rootData = matches[0]?.data as
		| { requestInfo?: { origin: string; path: string } }
		| undefined
	const origin = rootData?.requestInfo?.origin ?? 'https://dealerproedge.com'
	const path = rootData?.requestInfo?.path ?? '/chat-widget-terms-of-use'
	const canonicalUrl = `${origin}${path}`

	return [
		{ title: MetaTitle },
		{ name: 'description', content: MetaDescription },
		{ name: 'robots', content: 'noindex, nofollow' },
		{ tagName: 'link', rel: 'canonical', href: canonicalUrl },
	]
}

// =============================================================================
// MARKDOWN CONTENT
// =============================================================================

const markdown = `# Dealer Pro Edge Chat Widget Terms of Use

**Last Updated:** April 15, 2026

## 1. Introduction
These Terms govern your use of the Dealer Pro Edge Chat Widget (live chat, AI-assisted messaging, and related features) on participating dealership websites. By using the Chat Widget, you agree to these Terms and our Chat Widget Privacy Notice. If you do not agree, do not use the Chat Widget.

These Terms supplement our main Terms and Conditions and apply only to the Chat Widget.

## 2. Description of the Chat Widget
The Chat Widget enables real-time communication with the dealership whose site you are visiting. It may include AI-generated or AI-assisted responses, human review or assistance, intelligent message routing, and follow-up options (including SMS).

## 3. AI Disclosure and Limitations
**Important:** Some or all responses in this Chat Widget may be generated or assisted by artificial intelligence. You are communicating with a system that may include automated technology.

**AI-generated content is provided for general informational purposes only.** It may contain errors, omissions, or inaccuracies and is not a substitute for professional advice. The Chat Widget does **not** provide legal, financial, tax, medical, insurance, or any other professional advice.

You must independently verify all important information — including pricing, vehicle availability, financing terms, incentives, specifications, and appointment details — directly with the dealership before making any decisions or transactions.

Dealer Pro Edge is not responsible for the accuracy of any dealership-supplied information or for the outcome of any transaction between you and the dealership.

## 4. Dealership Role
Dealer Pro Edge provides only the technology platform. The dealership is solely responsible for its vehicles, pricing, offers, services, responses to inquiries, and all business practices. Dealer Pro Edge is not a party to any sale, lease, financing, or other transaction between you and the dealership.

## 5. Recording, Monitoring, and Retention
By using the Chat Widget, you consent to the recording, storage, monitoring, review, and sharing of your conversations with Dealer Pro Edge, the dealership, and our authorized service providers. This is done for service delivery, quality assurance, training (including AI improvement), compliance, and recordkeeping.

## 6. SMS and Follow-Up Communications
If you provide your phone number and continue the conversation, you may receive follow-up messages (including SMS/texts) from the dealership or Dealer Pro Edge related to your inquiry or appointment.

Message and data rates may apply. To stop receiving texts, reply **STOP** or contact the dealership / Dealer Pro Edge directly.

**Marketing messages** require separate prior express written consent consistent with applicable law (including the TCPA where relevant). Providing your number during the chat constitutes consent for transactional or inquiry-related communications, but not for marketing unless you expressly opt in.

## 7. Information You Submit (Customer Data)
You are responsible for the accuracy and legality of all information you provide ("Customer Data"). Do not submit false, misleading, unlawful, abusive, harassing, defamatory, obscene, harmful, malware-containing, or spam content. Avoid submitting highly sensitive personal information (e.g., full Social Security numbers, financial account details, medical information) unless expressly requested and necessary.

As between you and Dealer Pro Edge, you retain ownership of your Customer Data. You grant us a worldwide, non-exclusive, royalty-free license to access, store, reproduce, process, transmit, and use your Customer Data as necessary to provide, maintain, improve, and support the Chat Widget (including AI training and de-identification).

## 8. Permitted Use and Restrictions
Use the Chat Widget only for lawful purposes related to communicating with the dealership. We may suspend, restrict, or block access for misuse, abuse, spam, or violation of these Terms.

## 9. No Warranty / "As Is"
The Chat Widget is provided “AS IS” and “AS AVAILABLE” without any warranties of any kind. We do not guarantee availability, uninterrupted service, accuracy of responses, or that the Chat Widget will meet your specific needs.

## 10. Intellectual Property
The Chat Widget and all underlying technology are owned by Dealer Pro Edge or its licensors. You may not copy, reverse engineer, modify, or commercially exploit any part of it.

Any feedback or suggestions you provide regarding the Chat Widget is assigned to us (or you grant us a perpetual, royalty-free license to use it without restriction).

## 11. Confidentiality
To the extent any information shared through the Chat Widget constitutes Confidential Information, each party agrees to maintain its confidentiality and use it solely for the purposes of the chat. If disclosure is compelled by law, the receiving party will provide prior notice (where legally permitted) and reasonable assistance so the disclosing party may seek a protective order. Upon termination or request, Confidential Information will be returned or destroyed.

## 12. Limitation of Liability
To the fullest extent permitted by law, Dealer Pro Edge’s total liability arising from your use of the Chat Widget will not exceed US $100 (or the minimum amount required by applicable law).

We are not liable for any indirect, consequential, punitive, or incidental damages, including any reliance on AI-generated responses or dealership information.

## 13. Indemnification
You agree to indemnify and hold harmless Dealer Pro Edge and its affiliates, officers, agents, and employees from any claims, losses, or damages arising from your use of the Chat Widget, your submitted content, or your violation of these Terms.

## 14. Governing Law and Venue
These Terms are governed by the laws of the State of Connecticut without regard to conflict of laws principles. Any disputes will be resolved exclusively in the state or federal courts located in Connecticut, to the extent permitted by law. (Note: This can be updated later as you expand to other states.)

## 15. Changes to These Terms
We may update these Terms from time to time. Continued use of the Chat Widget after changes constitutes acceptance. Material changes will be noted with a new “Last Updated” date.

## 16. Contact Us
Dealer Pro Edge, LLC  
142 Swendsen Drive  
Monroe, CT 06468  
Email: support@dealerproedge.com
`

// =============================================================================
// LOADER
// =============================================================================

export async function loader() {
	const html = await marked.parse(markdown)
	return data({ html })
}

// =============================================================================
// ROUTE COMPONENT
// =============================================================================

export default function ChatWidgetTermsOfUseRoute({
	loaderData,
}: Route.ComponentProps) {
	return (
		<main className="mx-auto max-w-4xl px-4 py-10 sm:px-6 lg:px-8">
			<article
				className="prose prose-neutral max-w-none [&_h2]:mb-2 [&_h2]:mt-5"
				dangerouslySetInnerHTML={{ __html: loaderData.html }}
			/>
		</main>
	)
}
